import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  Wrench,
  Loader2,
  DollarSign,
  Calendar,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays, parseISO } from "date-fns";
import { fmtTz } from "../lib/time";

const SERVICE_TYPES = [
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'certification', label: 'Certification' },
  { value: 'inspection', label: 'Inspection' }
];

export default function AdminServiceRecords() {
  const { authAxios } = useAuth();
  const [records, setRecords] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterEquipment, setFilterEquipment] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [formData, setFormData] = useState({
    equipment_id: '',
    service_type: 'maintenance',
    description: '',
    technician: '',
    cost: '',
    notes: '',
    next_service_due: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [recordsRes, equipRes] = await Promise.all([
        authAxios.get('/equipment/service-records'),
        authAxios.get('/equipment')
      ]);
      setRecords(recordsRes.data);
      setEquipment(equipRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      equipment_id: '',
      service_type: 'maintenance',
      description: '',
      technician: '',
      cost: '',
      notes: '',
      next_service_due: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.equipment_id || !formData.description || !formData.technician) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/equipment/service-records', {
        ...formData,
        cost: parseFloat(formData.cost) || 0,
        next_service_due: formData.next_service_due || null
      });
      toast.success('Service record added');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add record');
    } finally {
      setSubmitting(false);
    }
  };

  const getDaysUntilService = (nextDue) => {
    if (!nextDue) return null;
    try {
      return differenceInDays(parseISO(nextDue), new Date());
    } catch {
      return null;
    }
  };

  const getServiceDueBadge = (equip) => {
    const days = getDaysUntilService(equip.next_service_due);
    if (days === null) return null;
    if (days < 0) return <Badge className="bg-rose-100 text-rose-700">Overdue</Badge>;
    if (days <= 7) return <Badge className="bg-amber-100 text-amber-700">Due Soon</Badge>;
    if (days <= 30) return <Badge className="bg-blue-100 text-blue-700">Upcoming</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700">On Track</Badge>;
  };

  const filteredRecords = records.filter(r => {
    const matchesEquipment = filterEquipment === 'all' || r.equipment_id === filterEquipment;
    const matchesType = filterType === 'all' || r.service_type === filterType;
    return matchesEquipment && matchesType;
  });

  const totalCost = filteredRecords.reduce((sum, r) => sum + (r.cost || 0), 0);

  // Equipment with upcoming service
  const equipmentNeedingService = equipment.filter(e => {
    const days = getDaysUntilService(e.next_service_due);
    return days !== null && days <= 30;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="service-records">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Service Records</h1>
          <p className="text-stone-500 mt-1">Equipment maintenance and service history</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-service-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Service Record
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-emerald-600" />
                New Service Record
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Equipment *</Label>
                <Select value={formData.equipment_id} onValueChange={(v) => setFormData({ ...formData, equipment_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select equipment" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipment.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name} ({e.equipment_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service Type *</Label>
                  <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service performed..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Technician *</Label>
                  <Input
                    value={formData.technician}
                    onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                    placeholder="Technician name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Service Due</Label>
                  <Input
                    type="date"
                    value={formData.next_service_due}
                    onChange={(e) => setFormData({ ...formData, next_service_due: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Record
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Service Due Alerts */}
      {equipmentNeedingService.length > 0 && (
        <Card className="border-0 shadow-lg mb-6 bg-amber-50 border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Service Due Soon</p>
                <p className="text-sm text-amber-700">
                  {equipmentNeedingService.length} equipment item(s) need service within 30 days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Stats */}
      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-xs text-stone-500">Filter by Equipment</Label>
              <Select value={filterEquipment} onValueChange={setFilterEquipment}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Equipment</SelectItem>
                  {equipment.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs text-stone-500">Filter by Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {SERVICE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-emerald-50 px-4 py-2 rounded-lg">
              <p className="text-xs text-emerald-600">Total Cost</p>
              <p className="text-xl font-bold text-emerald-700">${totalCost.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Records Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Wrench className="w-5 h-5 text-stone-400" />
            Service History ({filteredRecords.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No service records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Equipment</TableHead>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Next Due</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.equipment_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{record.service_type?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{record.description}</TableCell>
                      <TableCell>{record.technician}</TableCell>
                      <TableCell className="font-medium">${record.cost?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>
                        {record.next_service_due ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{fmtTz(record.next_service_due, 'MMM d, yyyy')}</span>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{fmtTz(record.created_at, 'MMM d, yyyy')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
